import { Link as RouterLink, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { safeMarkdownProps } from '../lib/safeMarkdown';
import remarkGfm from 'remark-gfm';
import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconBrain,
  IconCheck,
  IconClock,
  IconExternalLink,
  IconRefresh,
  IconUser,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KianUsageLine, kianUsageFromCall } from './KianUsageLine';
import { api } from '../lib/api';
import type { TechnicianBriefing } from '../lib/api';
import { useAuthStore } from '../stores/auth';
import { linkifyTicketNumbers } from './DailyBriefingPanel';
import { InjectionWarningAlert } from './KianAnalysisPanel';

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}


function formatHours(h: number): string {
  return `${h.toFixed(1).replace('.', ',')} h`;
}

export function PersonalBriefingContent({ briefing }: { briefing: TechnicianBriefing }) {
  const navigate = useNavigate();
  const s = briefing.stats;

  return (
    <Stack gap="sm">
      <Group gap={6}>
        <Badge variant="light" color="orange">
          {s.openOwn} deine offenen
        </Badge>
        {s.assignedUntouched > 0 && (
          <Badge variant="light" color="yellow">
            {s.assignedUntouched} unberührt
          </Badge>
        )}
        {s.waiting > 0 && (
          <Badge variant="light" color="gray">
            {s.waiting} wartend
          </Badge>
        )}
        {s.stale > 0 && (
          <Badge variant="light" color="orange">
            {s.stale} still
          </Badge>
        )}
        {s.overdue > 0 && <Badge variant="filled" color="red">{s.overdue} überfällig</Badge>}
        {s.slaBreached > 0 && <Badge variant="filled" color="red">{s.slaBreached} SLA</Badge>}
        {s.unassignedLast48h > 0 && (
          <Badge
            variant="light"
            color="violet"
            component="a"
            href="/tickets?tab=unassigned"
            target="_blank"
            rel="noopener noreferrer"
            style={{ cursor: 'pointer' }}
          >
            {s.unassignedLast48h} Pool (48 h)
          </Badge>
        )}
        <Badge variant="light" color="blue" leftSection={<IconClock size={10} />}>
          {formatHours(s.hoursLast24h)} / 24 h
        </Badge>
        {s.medianFirstResponseHours != null && (
          <Tooltip label={`Median Erstreaktion, n=${s.firstResponseSampleSize}`} withArrow>
            <Badge variant="outline" color="blue">
              Reaktion {formatHours(s.medianFirstResponseHours)}
            </Badge>
          </Tooltip>
        )}
        {s.pendingReminders > 0 && (
          <Badge variant="light" color="grape">{s.pendingReminders} Erinnerungen</Badge>
        )}
        {s.pendingHandovers > 0 && (
          <Badge variant="light" color="indigo">{s.pendingHandovers} Übergaben</Badge>
        )}
        {s.openCallbacks > 0 && (
          <Badge variant="light" color="cyan">{s.openCallbacks} Rückrufe</Badge>
        )}
        {(s.appointmentsToday ?? 0) > 0 && (
          <Badge variant="light" color="teal">{s.appointmentsToday} Termine heute</Badge>
        )}
        {((s.chatsAwaitingReply ?? 0) > 0 || (s.chatsUnassigned ?? 0) > 0) && (
          <Tooltip
            label={`${s.chatsAwaitingReply ?? 0} eigene + ${s.chatsUnassigned ?? 0} unzugewiesene Chats warten auf Antwort`}
            withArrow
          >
            <Badge
              variant="light"
              color="lime"
              component="a"
              href="/chats"
              target="_blank"
              rel="noopener noreferrer"
              style={{ cursor: 'pointer' }}
            >
              {(s.chatsAwaitingReply ?? 0) + (s.chatsUnassigned ?? 0)} Chats offen
            </Badge>
          </Tooltip>
        )}
        {s.lowBookingYesterday && (
          <Tooltip
            label={`Gestern (Werktag) nur ${formatHours(s.hoursYesterday ?? 0)} gebucht — Zeitbuchungen prüfen`}
            withArrow
          >
            <Badge variant="outline" color="yellow" style={{ cursor: 'help' }}>
              Buchungen gestern prüfen
            </Badge>
          </Tooltip>
        )}
        <Badge
          variant="light"
          color="green"
          leftSection={<IconCheck size={10} />}
        >
          {s.closedLast48h} erledigt (48 h)
        </Badge>
      </Group>

      <InjectionWarningAlert
        blocked={s.injectionBlocked ?? 0}
        flagged={s.injectionFlagged ?? 0}
        subjectGenitive="dieses Briefings"
      />

      <Box style={{ fontSize: 'var(--mantine-font-size-sm)', lineHeight: 1.6 }}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          {...safeMarkdownProps}
          components={{
            a: ({ href, children }) => {
              if (href?.startsWith('/t/')) {
                return (
                  <Anchor
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontFamily: 'monospace',
                      fontWeight: 700,
                      color: 'var(--fb-orange, orange)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {children}
                  </Anchor>
                );
              }
              if (href?.startsWith('/')) {
                return (
                  <Anchor component="button" onClick={() => navigate(href)}>
                    {children}
                  </Anchor>
                );
              }
              return (
                <Anchor href={href} target="_blank" rel="noopener noreferrer">
                  {children}
                </Anchor>
              );
            },
            h2: ({ children }) => (
              <Text size="md" fw={700} mt={14} mb={4} c="violet.7">{children}</Text>
            ),
            h3: ({ children }) => <Text size="sm" fw={700} mt={8} mb={2}>{children}</Text>,
            p: ({ children }) => <Text size="sm" mb={6}>{children}</Text>,
            li: ({ children }) => (
              <Text component="li" size="sm" style={{ marginLeft: 16, marginBottom: 3 }}>
                {children}
              </Text>
            ),
            strong: ({ children }) => (
              <Text component="span" size="sm" fw={700}>{children}</Text>
            ),
          }}
        >
          {linkifyTicketNumbers(briefing.briefingText)}
        </ReactMarkdown>
      </Box>

      <Group gap="xs" justify="space-between" wrap="wrap">
        <Text size="xs" c="dimmed">
          {briefing.technicianName} · erstellt {formatDateTime(briefing.createdAt)}
          {briefing.autoTriggered
            ? ' · automatischer 07:00-Bericht'
            : ` · angefordert von ${briefing.triggeredByName ?? 'Unbekannt'}`}
        </Text>
        <KianUsageLine usage={kianUsageFromCall(s.llm)} style={{ cursor: 'help' }} />
      </Group>
    </Stack>
  );
}

export function PersonalBriefingPanel({
  compact = false,
  technicianId,
  onTechnicianChange,
  showTechnicianSelect = true,
}: {
  compact?: boolean;
  technicianId?: string | null;
  onTechnicianChange?: (id: string | null) => void;
  showTechnicianSelect?: boolean;
}) {
  const queryClient = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const targetId = technicianId ?? me?.id ?? null;
  const isOwn = !targetId || targetId === me?.id;

  const { data: technicians } = useQuery({
    queryKey: ['briefing-technicians'],
    queryFn: () => api.getBriefingTechnicians(),
    staleTime: 60_000,
    enabled: showTechnicianSelect,
  });

  const { data: status, isLoading } = useQuery({
    queryKey: ['personal-briefing-status', targetId],
    queryFn: () => api.getPersonalBriefingStatus(isOwn ? undefined : targetId!),
    staleTime: 30_000,
    enabled: !!targetId,
  });

  const generateMutation = useMutation({
    mutationFn: () => api.generatePersonalBriefing(isOwn ? undefined : targetId!),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['personal-briefing-status'] });
    },
  });

  const briefing = generateMutation.data?.briefing ?? status?.latest ?? null;
  const isGenerating = generateMutation.isPending || (status?.generating ?? false);
  const noChanges = !!status?.latest && status.changesSinceLatest === 0;
  const limitReached = (status?.manualRemainingToday ?? 1) <= 0;
  const noWeclapp = status ? !status.hasWeclappLink : false;
  const canGenerate = !isGenerating && !noChanges && !limitReached && !noWeclapp;

  const buttonHint = noWeclapp
    ? 'Kein weclapp-Konto verknüpft — Briefing nicht möglich.'
    : noChanges
      ? 'Keine relevanten Änderungen seit dem letzten Briefing.'
      : limitReached
        ? `Tageslimit erreicht (${status?.manualDailyLimit ?? 3} persönliche Briefings pro Tag).`
        : `${status?.changesSinceLatest ?? 0} Änderungen seit dem letzten Briefing.`;

  const techOptions = (technicians?.technicians ?? []).map((t) => ({
    value: t.id,
    label: `${t.displayName}${t.id === me?.id ? ' (du)' : ''}${t.hasWeclappLink ? '' : ' — ohne weclapp'}`,
    disabled: !t.hasWeclappLink && t.id !== me?.id,
  }));

  const title = isOwn
    ? 'Dein Briefing'
    : `Briefing: ${status?.technicianName ?? 'Kollege'}`;

  const body = (
    <Stack gap="sm">
      <Group justify="space-between" align="center" wrap="wrap">
        <Group gap="xs">
          <IconUser size={18} color="var(--mantine-color-violet-6)" />
          <Text fw={700} c="violet.7">
            {title}
          </Text>
        </Group>
        <Group gap="xs" wrap="wrap">
          {showTechnicianSelect && !compact && (
            <Select
              placeholder="Kollegen wählen…"
              data={techOptions}
              value={targetId}
              onChange={onTechnicianChange}
              searchable
              clearable
              size="xs"
              style={{ width: 220 }}
            />
          )}
          {isGenerating && (
            <Group gap={6}>
              <Loader size="xs" color="violet" />
              <Text size="xs" c="dimmed">KIAN schreibt das Briefing…</Text>
            </Group>
          )}
          {!isGenerating && noChanges && !noWeclapp && (
            <Badge variant="light" color="green" leftSection={<IconCheck size={10} />}>
              Stand aktuell
            </Badge>
          )}
          {!isGenerating && status && (
            <Tooltip label={buttonHint} withArrow multiline w={280}>
              <span>
                <Button
                  size="compact-sm"
                  variant="light"
                  color="violet"
                  leftSection={<IconRefresh size={13} />}
                  disabled={!canGenerate}
                  onClick={() => generateMutation.mutate()}
                >
                  Aktualisieren (noch {status.manualRemainingToday} von {status.manualDailyLimit})
                </Button>
              </span>
            </Tooltip>
          )}
          {compact && (
            <Button
              size="compact-xs"
              variant="subtle"
              component={RouterLink}
              to="/briefing?view=personal"
              rightSection={<IconExternalLink size={12} />}
            >
              Vollansicht
            </Button>
          )}
        </Group>
      </Group>

      {isLoading && !briefing && (
        <Group gap="xs" justify="center" py="lg">
          <Loader size="sm" color="violet" />
        </Group>
      )}

      {noWeclapp && !isLoading && (
        <Alert color="gray" variant="light" p="xs" icon={<IconAlertTriangle size={16} />}>
          <Text size="xs" c="dimmed">
            Für diesen Account ist kein weclapp-Benutzer hinterlegt — KIAN kann kein
            persönliches Briefing bauen.
          </Text>
        </Alert>
      )}

      {generateMutation.isError && (
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />} p="xs">
          <Text size="xs">
            Briefing konnte nicht erstellt werden:{' '}
            {generateMutation.error instanceof Error ? generateMutation.error.message : 'Fehler'}
            {briefing ? ' — unten steht der letzte verfügbare Stand.' : ''}
          </Text>
        </Alert>
      )}

      {briefing ? (
        compact ? (
          <ScrollArea.Autosize mah={360} type="auto">
            <PersonalBriefingContent briefing={briefing} />
          </ScrollArea.Autosize>
        ) : (
          <PersonalBriefingContent briefing={briefing} />
        )
      ) : (
        !isLoading &&
        !isGenerating &&
        !noWeclapp && (
          <Alert color="gray" variant="light" p="xs" icon={<IconBrain size={16} />}>
            <Text size="xs" c="dimmed">
              Noch kein persönliches Briefing — mit „Aktualisieren“ jetzt erstellen. Ab morgen
              kommt um 07:00 automatisch eines.
            </Text>
          </Alert>
        )
      )}
    </Stack>
  );

  return (
    <Paper withBorder p="md" radius="md" style={{ borderColor: 'var(--mantine-color-violet-3)' }}>
      {body}
    </Paper>
  );
}
